<template>
  <div>
    <!-- Hero Header -->
    <div :style="heroStyle">
      <v-toolbar flat color="transparent" dark style="padding-top: 4px;">
        <v-btn icon @click="$router.back()">
          <v-icon small color="white">fas fa-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>
          <div style="font-weight: 900; letter-spacing: 0.2px;">Investment Details</div>
          <div style="font-size: 0.8rem; opacity: 0.9;">#{{ investment.id || '-' }}</div>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip dark small :color="statusColor" style="font-weight: 600; border-radius: 999px;">
          <v-icon x-small left>{{ statusIcon }}</v-icon>
          {{ statusText }}
        </v-chip>
        <v-btn icon class="ml-2" :to="'/savings'">
          <v-icon small color="white">fas fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-container class="py-0">
        <v-row class="pb-4 align-end">
          <v-col cols="12" md="8">
            <div style="font-size: 1.8rem; font-weight: 900; letter-spacing: 0.3px;">
              {{ formatAmount(investment.investment_amount) }} FCFA
            </div>
            <div style="font-size: 0.85rem; opacity: 0.9; margin-top: 4px;">
              <span v-if="investment.start_date">
                <v-icon x-small color="white">fas fa-calendar</v-icon> {{ investment.start_date }}
              </span>
              <span v-if="investment.start_date && investment.end_date" style="margin: 0 8px; opacity: 0.6;">•</span>
              <span v-if="investment.end_date">Ends {{ investment.end_date }}</span>
              <span v-if="investment.next_due_date" style="margin: 0 8px; opacity: 0.6;">•</span>
              <span v-if="investment.next_due_date">Next due {{ investment.next_due_date }}</span>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div style="min-width: 200px;">
              <div class="d-flex justify-space-between mb-1">
                <span style="font-size: 0.75rem; opacity: 0.8;">Progress</span>
                <span style="font-size: 0.75rem; font-weight: 700;">{{ progressPercentage }}%</span>
              </div>
              <v-progress-linear
                :value="progressPercentage"
                height="10"
                rounded
                color="white"
                background-color="rgba(255,255,255,0.25)"
              ></v-progress-linear>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Content -->
    <v-container class="investment-detail-container">
      <!-- Stats -->
      <v-row class="mb-4">
        <v-col cols="6" md="3" v-for="(stat, i) in stats" :key="i">
          <v-card
            flat
            style="border-radius: 14px; transition: all 0.25s ease;"
            :style="`background: ${stat.bg}; border-left: 4px solid ${stat.color};`"
            @mouseenter="$event.currentTarget.style.transform='translateY(-4px)'; $event.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.08)'"
            @mouseleave="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.boxShadow='none'"
          >
            <v-card-text class="text-center pa-3">
              <v-icon size="28" :color="stat.color">{{ stat.icon }}</v-icon>
              <div style="font-size: 1.15rem; font-weight: 800; color: #333; margin-top: 4px;">{{ stat.value }}</div>
              <div style="font-size: 0.65rem; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px;">{{ stat.label }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Next Payment Action -->
      <v-card
        v-if="nextPaymentAction"
        class="mb-4"
        style="border-radius: 16px; border: 2px solid #00897b; background: linear-gradient(135deg, #e0f2f1 0%, #ffffff 100%); animation: pulse-border 2s infinite;"
      >
        <v-card-text class="pa-4 pa-md-5">
          <v-row align="center">
            <v-col cols="12" md="7">
              <h2 style="font-size: 1.25rem; font-weight: 700; margin-bottom: 8px;">
                <v-icon large :color="nextPaymentAction.color || '#00897b'" class="mr-2">{{ nextPaymentAction.icon }}</v-icon>
                {{ nextPaymentAction.title }}
              </h2>
              <p style="color: #555; margin-bottom: 12px;">{{ nextPaymentAction.description }}</p>

              <v-row class="mt-2">
                <v-col cols="6" sm="4" class="py-1">
                  <div style="background: white; padding: 10px; border-radius: 10px;">
                    <div style="font-size: 0.7rem; color: #888; font-weight: 600; text-transform: uppercase;">Amount</div>
                    <div style="font-size: 1.1rem; font-weight: 800; color: #333;">{{ formatAmount(nextPaymentAction.amount) }} FCFA</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" class="py-1" v-if="nextPaymentAction.dueDate">
                  <div style="background: white; padding: 10px; border-radius: 10px;">
                    <div style="font-size: 0.7rem; color: #888; font-weight: 600; text-transform: uppercase;">Due Date</div>
                    <div style="font-size: 1.1rem; font-weight: 800; color: #333;">{{ formatDate(nextPaymentAction.dueDate) }}</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="4" class="py-1" v-if="nextPaymentAction.penalty > 0">
                  <div style="background: white; padding: 10px; border-radius: 10px;">
                    <div style="font-size: 0.7rem; color: #c62828; font-weight: 600; text-transform: uppercase;">Penalty</div>
                    <div style="font-size: 1.1rem; font-weight: 800; color: #c62828;">{{ formatAmount(nextPaymentAction.penalty) }} FCFA</div>
                  </div>
                </v-col>
              </v-row>

              <v-alert
                v-if="nextPaymentAction.warning"
                dense
                text
                :type="nextPaymentAction.warningType"
                class="mt-3 mb-0"
                style="border-radius: 10px;"
              >
                <v-icon x-small left>{{ nextPaymentAction.warningIcon }}</v-icon>
                {{ nextPaymentAction.warning }}
              </v-alert>

              <v-chip
                v-if="nextPaymentAction.paymentId && nextPaymentAction.canBePartial"
                small
                style="background: #e3f2fd; color: #1565c0; margin-top: 10px; font-weight: 500;"
              >
                <v-icon x-small left>fas fa-info-circle</v-icon>
                Partial payments accepted
              </v-chip>
            </v-col>

            <v-col cols="12" md="5" class="text-center text-md-right mt-3 mt-md-0">
              <v-btn
                x-large
                :color="nextPaymentAction.buttonColor || '#00897b'"
                class="white--text elevation-6"
                style="padding: 0 36px; font-size: 16px; font-weight: 700; border-radius: 12px;"
                @click="makePayment(nextPaymentAction.paymentId)"
                :disabled="nextPaymentAction.disabled"
              >
                <v-icon left>{{ nextPaymentAction.buttonIcon }}</v-icon>
                {{ nextPaymentAction.buttonText }}
              </v-btn>
              <p v-if="nextPaymentAction.info" style="font-size: 0.75rem; color: #888; margin-top: 8px;" class="mb-0">
                {{ nextPaymentAction.info }}
              </p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Recent Payments -->
      <v-card v-if="recentPayments.length > 0" class="mb-4" style="border-radius: 16px; overflow: hidden;" elevation="3">
        <div class="d-flex align-center pa-3" style="background: #2e7d32; color: white;">
          <v-icon color="white" class="mr-2">fas fa-receipt</v-icon>
          <span style="font-weight: 700;">Recent Payments</span>
          <v-spacer></v-spacer>
          <v-chip small style="background: white; color: #2e7d32; font-weight: 700;">
            {{ recentPayments.length }}
          </v-chip>
        </div>
        <v-list dense>
          <v-list-item v-for="(tx, idx) in recentPayments" :key="tx.id">
            <v-list-item-avatar>
              <v-icon :color="getTransactionColor(tx.transaction_type)">{{ getTransactionIcon(tx.transaction_type) }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title style="font-weight: 700;">{{ tx.description }}</v-list-item-title>
              <v-list-item-subtitle style="font-size: 0.8rem; color: #888;">{{ formatDateTime(tx.transaction_date) }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip small dark :color="getTransactionColor(tx.transaction_type)" style="font-weight: 600;">
                {{ getTransactionSign(tx.transaction_type) }}{{ formatAmount(tx.amount) }} FCFA
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-divider v-if="transactions.length > 3"></v-divider>
        <v-card-actions v-if="transactions.length > 3" class="pa-2">
          <v-spacer></v-spacer>
          <v-btn text small color="#00695C" @click="showAllTransactions = true">
            View All ({{ transactions.length }})
            <v-icon right x-small>fas fa-arrow-down</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Payment Schedule (monthly) -->
      <v-card v-if="investment.payment_type === 'monthly'" class="mb-4" style="border-radius: 16px; overflow: hidden;" elevation="3">
        <div class="d-flex align-center pa-3" style="background: #6a1b9a; color: white;">
          <v-icon color="white" class="mr-2">fas fa-calendar-alt</v-icon>
          <span style="font-weight: 700;">Payment Schedule (10 Months)</span>
        </div>
        <v-data-table
          :headers="scheduleHeaders"
          :items="payments"
          hide-default-footer
          dense
          style="border-radius: 0;"
        >
          <template #[`item.month_number`]="{ item }">
            <v-chip x-small dark :color="getMonthColor(item.month_number)" style="font-weight: 700;">M{{ item.month_number }}</v-chip>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip x-small dark :color="getPaymentStatusColor(item.status)" style="font-weight: 500;">
              <v-icon x-small left>{{ getPaymentStatusIcon(item.status) }}</v-icon>
              {{ getPaymentStatusLabel(item.status) }}
            </v-chip>
          </template>
          <template #[`item.due_date`]="{ item }">
            <span style="font-size: 0.85rem; color: #555;">{{ formatDate(item.due_date) }}</span>
          </template>
          <template #[`item.amounts`]="{ item }">
            <div class="d-flex flex-column py-1">
              <span style="font-weight: 700; color: #333;">{{ formatAmount(item.amount_due) }} FCFA</span>
              <span v-if="item.amount_paid > 0" style="font-size: 0.75rem; color: #2e7d32;">Paid: {{ formatAmount(item.amount_paid) }}</span>
              <span v-if="item.remaining_amount > 0" style="font-size: 0.75rem; color: #e65100;">Remaining: {{ formatAmount(item.remaining_amount) }}</span>
            </div>
          </template>
          <template #[`item.penalty`]="{ item }">
            <span v-if="item.penalty_applied > 0" style="color: #c62828; font-weight: 700; font-size: 0.85rem;">{{ formatAmount(item.penalty_applied) }}</span>
            <span v-else style="color: #888; font-size: 0.85rem;">-</span>
          </template>
          <template #[`item.action`]="{ item }">
            <div class="d-flex justify-end">
              <v-icon v-if="item.status === 'completed'" color="#2e7d32" size="28">fas fa-check-circle</v-icon>
              <v-btn
                v-else-if="canPayThisMonth(item)"
                x-small
                :color="item.status === 'partial' ? '#ef6c00' : '#00897b'"
                class="white--text"
                style="font-weight: 600; text-transform: none; letter-spacing: 0;"
                @click="makePayment(item.id)"
              >
                <v-icon x-small left>{{ item.status === 'partial' ? 'fas fa-plus' : 'fas fa-credit-card' }}</v-icon>
                {{ item.status === 'partial' ? 'Continue' : 'Pay' }}
              </v-btn>
              <v-tooltip v-else top>
                <template #activator="{ on }">
                  <v-icon color="#bdbdbd" v-on="on">fas fa-lock</v-icon>
                </template>
                <span>Complete previous payments first</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Transaction History -->
      <v-card class="mb-4" style="border-radius: 16px; overflow: hidden;" elevation="3">
        <div class="d-flex align-center pa-3" style="background: #1565c0; color: white;">
          <v-icon color="white" class="mr-2">fas fa-history</v-icon>
          <span style="font-weight: 700;">Transaction History</span>
          <v-spacer></v-spacer>
          <v-btn v-if="showAllTransactions && recentPayments.length > 0" icon dark x-small @click="showAllTransactions = false">
            <v-icon x-small>fas fa-times</v-icon>
          </v-btn>
        </div>
        <v-data-table
          :headers="transactionHeaders"
          :items="transactions"
          :items-per-page="10"
          :no-data-text="'No transactions yet'"
          class="elevation-0"
          hide-default-footer
          dense
        >
          <template #[`item.icon`]="{ item }">
            <v-icon :color="getTransactionColor(item.transaction_type)" size="22">{{ getTransactionIcon(item.transaction_type) }}</v-icon>
          </template>
          <template #[`item.description`]="{ item }">
            <div style="font-weight: 700; color: #333; font-size: 0.9rem;">{{ item.description }}</div>
            <div style="font-size: 0.75rem; color: #888;">{{ formatDateTime(item.transaction_date) }}</div>
          </template>
          <template #[`item.transaction_type`]="{ item }">
            <v-chip x-small dark :color="getTransactionColor(item.transaction_type)" style="font-weight: 500;">
              <v-icon x-small left>{{ getTransactionIcon(item.transaction_type) }}</v-icon>
              {{ formatTransactionType(item.transaction_type) }}
            </v-chip>
          </template>
          <template #[`item.amount`]="{ item }">
            <span :style="`font-weight: 800; ${getAmountStyle(item.transaction_type)}`">
              {{ getTransactionSign(item.transaction_type) }}{{ formatAmount(item.amount) }} FCFA
            </span>
          </template>
          <template #no-data>
            <div class="text-center pa-6">
              <v-icon size="48" color="#e0e0e0">fas fa-inbox</v-icon>
              <div style="color: #888; font-weight: 700; margin-top: 8px;">No transactions yet</div>
              <div style="color: #bbb; font-size: 0.85rem;">Transactions will appear here when payments are made</div>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- View History CTA -->
      <v-card class="mb-4 text-center pa-4" style="border-radius: 16px; background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%); border: 1px solid #bbdefb;" flat>
        <v-icon size="40" color="#1565c0">fas fa-list-alt</v-icon>
        <h3 style="color: #1565c0; font-weight: 700; margin: 8px 0 4px;">Complete Payment History</h3>
        <p style="color: #888; font-size: 0.9rem; margin-bottom: 12px;">See all your payment transactions including validated, pending, and cancelled payments</p>
        <v-btn
          large
          color="#1565c0"
          class="white--text"
          style="border-radius: 10px; font-weight: 600; text-transform: none; letter-spacing: 0;"
          :to="`/savings/investments/${investment.id}/payments-history`"
        >
          <v-icon left>fas fa-list-alt</v-icon>
          View Payment History
        </v-btn>
      </v-card>

      <!-- Overall Progress -->
      <v-card class="pa-4 mb-4" style="border-radius: 16px;" flat elevation="2">
        <h3 style="color: #00695c; font-weight: 700; margin-bottom: 12px;">
          <v-icon color="#00695c" class="mr-2">fas fa-tasks</v-icon>
          Overall Progress
        </h3>
        <v-progress-linear
          :value="progressPercentage"
          height="24"
          rounded
          color="#00897b"
          striped
          style="border-radius: 12px;"
        >
          <strong style="color: white; font-size: 0.9rem;">{{ progressPercentage }}%</strong>
        </v-progress-linear>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      investment: {},
      payments: [],
      transactions: [],
      showAllTransactions: false,
      scheduleHeaders: [
        { text: 'Month', value: 'month_number', sortable: false, align: 'center', width: '80px' },
        { text: 'Due Date', value: 'due_date', sortable: false },
        { text: 'Status', value: 'status', sortable: false, align: 'center' },
        { text: 'Amounts', value: 'amounts', sortable: false },
        { text: 'Penalty', value: 'penalty', sortable: false, align: 'center' },
        { text: '', value: 'action', sortable: false, align: 'end', width: '100px' },
      ],
      transactionHeaders: [
        { text: '', value: 'icon', sortable: false, align: 'center', width: '50px' },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Type', value: 'transaction_type', sortable: false, align: 'center' },
        { text: 'Amount', value: 'amount', sortable: false, align: 'end' },
      ],
    }
  },

  computed: {
    heroStyle() {
      const gradients = {
        active: 'linear-gradient(135deg, #0f9d58 0%, #0b8043 100%)',
        completed: 'linear-gradient(135deg, #1a73e8 0%, #174ea6 100%)',
        cancelled: 'linear-gradient(135deg, #d93025 0%, #a50e0e 100%)',
        pending_first_payment: 'linear-gradient(135deg, #f29900 0%, #e37400 100%)',
      }
      return {
        background: gradients[this.investment.status] || gradients.pending_first_payment,
        color: 'white',
        borderRadius: '0 0 24px 24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      }
    },

    stats() {
      return [
        { icon: 'fas fa-coins', value: this.formatAmount(this.investment.total_paid), label: 'Total Paid', color: '#00695C', bg: '#E0F2F1' },
        { icon: 'fas fa-percentage', value: this.formatAmount(this.investment.interest_earned), label: 'Interest', color: '#2E7D32', bg: '#E8F5E9' },
        { icon: 'fas fa-exclamation-triangle', value: this.formatAmount(this.investment.total_penalties), label: 'Penalties', color: '#EF6C00', bg: '#FFF3E0' },
        { icon: 'fas fa-chart-line', value: this.progressPercentage + '%', label: 'Progress', color: '#6A1B9A', bg: '#F3E5F5' },
      ]
    },

    statusColor() {
      const colors = { active: '#2E7D32', completed: '#1565C0', cancelled: '#C62828', pending_first_payment: '#E65100' }
      return colors[this.investment.status] || '#757575'
    },

    statusIcon() {
      const icons = { active: 'fas fa-check-circle', completed: 'fas fa-trophy', cancelled: 'fas fa-times-circle', pending_first_payment: 'fas fa-clock' }
      return icons[this.investment.status] || 'fas fa-question-circle'
    },

    statusText() {
      const texts = { active: 'Active', completed: 'Completed', cancelled: 'Cancelled', pending_first_payment: 'Pending First Payment' }
      return texts[this.investment.status] || 'Unknown'
    },

    progressPercentage() {
      if (this.investment.payment_type === 'one_time') {
        return this.investment.one_time_payment_made ? 100 : 0
      }
      const completed = this.payments.filter(p => p.status === 'completed').length
      return Math.round((completed / 10) * 100)
    },

    daysRemaining() {
      if (!this.investment.end_date) return 0
      const today = new Date()
      const endDate = new Date(this.investment.end_date)
      return Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)))
    },

    recentPayments() {
      return this.transactions
        .filter(t => t.transaction_type === 'payment' && t.amount > 0 && t.validation_status === 'Validated')
        .slice(0, 3)
    },

    nextPaymentAction() {
      if (!this.investment.id) return null

      // ONE-TIME
      if (this.investment.payment_type === 'one_time') {
        const hasValidated = this.transactions.some(t => t.transaction_type === 'payment' && t.validation_status === 'Validated' && t.amount > 0)
        if (hasValidated) return null

        const pendingPayments = this.transactions.filter(t => t.transaction_type === 'payment' && t.validation_status === 'Pending' && t.amount > 0)
        if (pendingPayments.length > 0) {
          return {
            title: '⏳ Payment Awaiting Validation',
            description: `You have ${pendingPayments.length} payment(s) waiting for manager validation. You can submit another payment if needed.`,
            amount: this.investment.investment_amount,
            dueDate: null,
            penalty: 0,
            warning: '📋 A manager will review your payment(s) soon. If you made an error, you can submit a new payment.',
            warningType: 'info',
            warningIcon: 'fas fa-info-circle',
            buttonText: 'Pay Again?',
            buttonColor: '#EF6C00',
            buttonIcon: 'fas fa-credit-card',
            color: '#1565C0',
            icon: 'fas fa-clock',
            info: `${pendingPayments.length} payment(s) pending validation`,
            paymentId: null,
            canBePartial: false,
            disabled: false,
          }
        }

        const creationDate = new Date(this.investment.creation_date)
        const deadline = new Date(creationDate.getTime() + 2 * 24 * 60 * 60 * 1000)
        const now = new Date()
        const hoursLeft = Math.max(0, Math.floor((deadline - now) / (1000 * 60 * 60)))

        return {
          title: 'One-Time Payment Required',
          description: 'Pay the full investment amount to activate your investment.',
          amount: this.investment.investment_amount,
          dueDate: null,
          penalty: 0,
          warning: hoursLeft > 0 ? `⏰ ${hoursLeft} hours remaining before cancellation!` : '⚠️ Payment deadline passed - Investment may be cancelled',
          warningType: hoursLeft > 0 ? 'warning' : 'error',
          warningIcon: 'fas fa-clock',
          buttonText: 'Pay Now',
          buttonColor: '#00897b',
          buttonIcon: 'fas fa-credit-card',
          color: '#00897b',
          icon: 'fas fa-money-bill-wave',
          info: 'Must be paid within 2 days of investment creation',
          paymentId: null,
          canBePartial: false,
          disabled: false,
        }
      }

      // MONTHLY
      if (this.investment.payment_type === 'monthly' && this.payments.length > 0) {
        const nextPayment = this.payments.find(p => p.status !== 'completed')
        if (!nextPayment) return null

        const today = new Date()
        const dueDate = new Date(nextPayment.due_date)
        const gracePeriod = new Date(nextPayment.grace_period_end)
        const isLate = today > gracePeriod
        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))
        const hasPending = nextPayment.pending_transactions > 0

        if (hasPending && nextPayment.status !== 'completed') {
          return {
            title: `⏳ Month ${nextPayment.month_number} - Awaiting Validation`,
            description: `You have ${nextPayment.pending_transactions} payment(s) waiting for manager validation.`,
            amount: nextPayment.remaining_amount,
            dueDate: nextPayment.due_date,
            penalty: nextPayment.penalty_applied,
            warning: nextPayment.validated_transactions > 0
              ? `✅ ${nextPayment.validated_transactions} validated. ${nextPayment.pending_transactions} pending.`
              : `📋 ${nextPayment.pending_transactions} payment(s) submitted, waiting for validation.`,
            warningType: 'info',
            warningIcon: 'fas fa-info-circle',
            buttonText: nextPayment.remaining_amount > 0 ? 'Pay Again?' : 'Waiting...',
            buttonColor: nextPayment.remaining_amount > 0 ? '#EF6C00' : '#9E9E9E',
            buttonIcon: nextPayment.remaining_amount > 0 ? 'fas fa-plus' : 'fas fa-clock',
            color: '#1565C0',
            icon: 'fas fa-clock',
            info: 'Payments will be validated by a manager',
            paymentId: nextPayment.remaining_amount > 0 ? nextPayment.id : null,
            canBePartial: nextPayment.month_number > 1,
            disabled: nextPayment.remaining_amount === 0,
          }
        }

        let warning = null
        let warningType = 'info'
        let warningIcon = 'fas fa-info-circle'

        if (nextPayment.month_number === 1 && this.investment.status === 'pending_first_payment') {
          const creationDate = new Date(this.investment.creation_date)
          const deadline = new Date(creationDate.getTime() + 2 * 24 * 60 * 60 * 1000)
          const hoursLeft = Math.max(0, Math.floor((deadline - today) / (1000 * 60 * 60)))
          warning = `⚠️ First payment MUST be complete within ${hoursLeft} hours or investment will be cancelled!`
          warningType = 'error'
          warningIcon = 'fas fa-exclamation-triangle'
        } else if (isLate) {
          warning = `⚠️ Payment is LATE! Penalty of ${this.formatAmount(nextPayment.penalty_applied)} FCFA applied.`
          warningType = 'error'
          warningIcon = 'fas fa-exclamation-triangle'
        } else if (daysUntilDue <= 3) {
          warning = `⏰ Payment due in ${daysUntilDue} day(s). Grace period ends on ${this.formatDate(nextPayment.grace_period_end)}.`
          warningType = 'warning'
          warningIcon = 'fas fa-clock'
        }

        return {
          title: nextPayment.status === 'partial' ? `Continue Payment - Month ${nextPayment.month_number}` : `Next Payment - Month ${nextPayment.month_number}`,
          description: nextPayment.status === 'partial'
            ? `You've paid ${this.formatAmount(nextPayment.amount_paid)} FCFA (validated). Complete the remaining amount.`
            : `Your ${this.getOrdinal(nextPayment.month_number)} monthly installment is ${isLate ? 'overdue' : 'due'}.`,
          amount: nextPayment.remaining_amount || nextPayment.amount_due + nextPayment.penalty_applied,
          dueDate: nextPayment.due_date,
          penalty: nextPayment.penalty_applied,
          warning,
          warningType,
          warningIcon,
          buttonText: nextPayment.status === 'partial' ? 'Continue Payment' : 'Pay Now',
          buttonColor: nextPayment.status === 'partial' ? '#EF6C00' : '#00897b',
          buttonIcon: 'fas fa-credit-card',
          color: isLate ? '#C62828' : (nextPayment.status === 'partial' ? '#EF6C00' : '#00897b'),
          icon: isLate ? 'fas fa-exclamation-triangle' : (nextPayment.status === 'partial' ? 'fas fa-clock' : 'fas fa-calendar-check'),
          info: nextPayment.month_number > 1 ? 'Partial payments accepted' : 'First payment must be complete',
          paymentId: nextPayment.id,
          canBePartial: nextPayment.month_number > 1,
          disabled: false,
        }
      }

      return null
    },
  },

  filters: {
    formatAmount(value) {
      return new Intl.NumberFormat('fr-FR').format(value)
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatTransactionType(value) {
      const types = { payment: 'Payment', penalty: 'Penalty', interest: 'Interest', transfer: 'Transfer' }
      return types[value] || (value ? value.charAt(0).toUpperCase() + value.slice(1) : '')
    },
  },

  methods: {
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '0'
      const n = Number(value)
      return Number.isNaN(n) ? String(value) : new Intl.NumberFormat('fr-FR').format(n)
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatTransactionType(value) {
      const types = { payment: 'Payment', penalty: 'Penalty', interest: 'Interest', transfer: 'Transfer' }
      return types[value] || (value ? value.charAt(0).toUpperCase() + value.slice(1) : '')
    },

    async loadInvestmentData() {
      this.$store.commit('setIsLoading', true)
      const investmentId = this.$route.params.investment_id
      try {
        const response = await axios.get(`/api/v1/manager/investments/investment-details/${investmentId}/`)
        this.investment = response.data.investment
        this.payments = response.data.payments || []
        this.transactions = response.data.transactions || []
      } catch (error) {
        this.$store.commit('setSnackBarMessage', 'Error loading investment')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
        setTimeout(() => this.$router.push('/savings/investments'), 2000)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    canPayThisMonth(payment) {
      if (payment.month_number === 1) return true
      const previousMonth = this.payments.find(p => p.month_number === payment.month_number - 1)
      return previousMonth && previousMonth.status === 'completed'
    },

    makePayment(paymentId) {
      if (this.nextPaymentAction && this.nextPaymentAction.disabled) {
        this.$store.commit('setSnackBarMessage', 'Please wait for payment validation')
        this.$store.commit('setSnackBarColor', 'orange darken-2')
        this.$store.commit('activateSnackBar', true)
        return
      }
      const route = paymentId
        ? `/savings/investments/pay/${this.investment.id}/${paymentId}`
        : `/savings/investments/pay/${this.investment.id}`
      this.$router.push(route)
    },

    getMonthColor(month) {
      const colors = ['#00897b', '#1565c0', '#2e7d32', '#6a1b9a', '#ef6c00', '#c62828', '#0277bd', '#00695c', '#f9a825', '#ff8f00']
      return colors[(month - 1) % colors.length]
    },

    getPaymentStatusColor(status) {
      const colors = { completed: '#2E7D32', partial: '#EF6C00', pending: '#757575', failed: '#C62828' }
      return colors[status] || '#757575'
    },

    getPaymentStatusIcon(status) {
      const icons = { completed: 'fas fa-check', partial: 'fas fa-clock', pending: 'fas fa-hourglass-half', failed: 'fas fa-times' }
      return icons[status] || 'fas fa-question'
    },

    getPaymentStatusLabel(status) {
      const labels = { completed: 'Paid', partial: 'Partial', pending: 'Pending', failed: 'Failed' }
      return labels[status] || status
    },

    getTransactionColor(type) {
      const colors = { payment: '#2E7D32', penalty: '#C62828', interest: '#00897b', transfer: '#1565C0' }
      return colors[type] || '#757575'
    },

    getTransactionIcon(type) {
      const icons = { payment: 'fas fa-arrow-down', penalty: 'fas fa-exclamation-triangle', interest: 'fas fa-percentage', transfer: 'fas fa-exchange-alt' }
      return icons[type] || 'fas fa-question'
    },

    getTransactionSign(type) {
      return type === 'penalty' ? '-' : '+'
    },

    getAmountStyle(type) {
      if (type === 'penalty') return 'color: #C62828;'
      if (['payment', 'interest', 'transfer'].includes(type)) return 'color: #2E7D32;'
      return 'color: #424242;'
    },

    getOrdinal(n) {
      const s = ['th', 'st', 'nd', 'rd']
      const v = n % 100
      return n + (s[(v - 20) % 10] || s[v] || s[0])
    },
  },

  mounted() {
    this.loadInvestmentData()
    document.title = 'PettyCash | Investment Details'
    this.$store.commit('setCloseURL', '/savings/investments')
  },
}
</script>

<style scoped>
.investment-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

@keyframes pulse-border {
  0%, 100% {
    border-color: #00897b;
    box-shadow: 0 0 0 0 rgba(0, 137, 123, 0.25);
  }
  50% {
    border-color: #004d40;
    box-shadow: 0 0 0 8px rgba(0, 137, 123, 0);
  }
}

::v-deep .v-data-table tbody tr {
  transition: background 0.15s ease;
}

::v-deep .v-data-table tbody tr:hover {
  background: #f5f5f5 !important;
}

::v-deep .v-data-table th {
  font-weight: 700 !important;
  font-size: 0.7rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555 !important;
  background: #fafafa !important;
}
</style>
